import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import throttle from 'lodash/throttle';
import { store } from '@/app/store';
import { restApi } from '@/services/api';
import {
  httpStatusOf,
  isNetworkFailure,
  sendKeepalive,
} from '@/services/api/http';
import { authConfig } from '@/services/auth/auth-config';
import { sentryService } from '@/services/telemetry/sentry';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';
import { track, warnMessageOf } from '@/shared/utils/track-telemetry';
import { ANALYTICS_METRICS } from '../configs/ANALYTICS';

/**
 * Fields the customer-download portal is allowed to write. Must be
 * kept in sync with the backend's CustomerDownloadEngagementRequest
 * DTO — if the backend grows a new field, drift here means the
 * frontend silently stops sending it. Pinned by a unit test so the
 * mismatch is caught at PR time.
 */
const CUSTOMER_DOWNLOAD_FIELDS = [
  'download_engaged',
  'download_download_clicked',
  'download_send_or_share_clicked',
  'time_download_initiated',
  'time_download_used',
];

function filterCustomerDownloadFields(data) {
  return pick(data, CUSTOMER_DOWNLOAD_FIELDS);
}

export { CUSTOMER_DOWNLOAD_FIELDS, filterCustomerDownloadFields };

// A network failure while the browser still reports online is not a phone
// losing signal: it is a CORS, DNS or proxy misconfiguration that would stay
// invisible as a breadcrumb. One event per customer session is enough to page
// on; resetForNewSession() re-arms it so a kiosk that runs for days is not
// silenced after its first report.
let onlineNetworkFailureReported = false;

export function resetOnlineNetworkFailureReport() {
  onlineNetworkFailureReported = false;
}

function rejectAfter(ms, message, onTimer) {
  return new Promise((_, reject) => {
    onTimer(setTimeout(() => reject(new Error(message)), ms));
  });
}

export class Analytics {
  METRICS = ANALYTICS_METRICS;

  /**
   * Delay during which analytics events are accumulated to be sent in a single chunk afterward.
   * Each submit is one write of the session row on the backend. The router
   * sends the queue on each step change, and Session.end sends it for the
   * ending session. beforeunload, pagehide and a hidden page send the rest with
   * a keepalive fetch. Thus a long window loses no metric.
   */
  SERVER_SUBMISSION_DELAY = 15_000;

  /**
   * Max number of distinct metric keys allowed in the submission queue.
   * The queue is keyed by metric name so duplicate keys overwrite, but on a kiosk
   * with network issues metrics from multiple tracking calls can accumulate.
   * When exceeded, a warning is logged and the queue is not allowed to grow further --
   * new keys are silently dropped (existing keys still update in place).
   */
  MAX_QUEUE_KEYS = 100;

  dataSubmissionQueue = {};
  storedTimeTo = {};
  storedIncrements = {};
  storedBooleans = {};
  isFlushInProgress = false;
  currentFlushPromise = null;

  /**
   * In-flight submission promise. When non-null, a submit is in progress.
   * Concurrent callers await this promise instead of starting a duplicate
   * submission that would snapshot the same queue and race on the omit.
   * @type {Promise|null}
   */
  _submitPromise = null;

  /** @type {number|null} Timer ID for the flush timeout, cleared on resetForNewSession */
  _flushTimeoutId = null;

  /** @type {number} Track total analytics submission failures for health monitoring */
  _submissionFailureCount = 0;

  /**
   * Session ID captured at queue-time. When submitAnalyticsData() fires (possibly
   * seconds later via throttle), it uses this captured ID instead of reading
   * store.sessionId, preventing session N data from being sent with session N+1's ID.
   * Reset to null in resetForNewSession().
   * @type {string|null}
   */
  _capturedSessionId = null;

  /**
   * Customer-download portal mode. When set, _doSubmit and trackError post
   * to the bounded-context /api/v1/customer/sessions/{download_code}/... endpoints
   * (no kiosk api_key required, auth is proof-of-possession of the
   * download_code in the URL). When null, the kiosk-operator endpoints
   * are used.
   *
   * Set via setCustomerDownloadCode(code) from
   * CustomerPortraitsDownloadCode.vue on mount. Cleared on
   * resetForNewSession() so a kiosk session that follows a customer
   * download view doesn't accidentally inherit the customer routing.
   * @type {string|null}
   */
  _customerDownloadCode = null;

  constructor() {
    exposeToGlobalScope(this, 'Analytics');
    this.submitAnalyticsDataThrottled = throttle(
      () => {
        void this.submitAnalyticsData().catch((error) =>
          warnMessageOf(
            '[Analytics] Background submission failed (will retry):',
            error
          )
        );
      },
      this.SERVER_SUBMISSION_DELAY,
      { leading: false, trailing: true }
    );
  }

  /**
   * Track time elapsed from session start to a milestone event.
   * Forwards the timing data to Sentry as both a breadcrumb and a metric.
   * @param {string} metric - Metric name from ANALYTICS_METRICS
   * @param {Object} options - Optional configuration (e.g., { immediate: true })
   * @returns {Promise|undefined}
   */
  trackTimeTo(metric, options) {
    if (this.storedTimeTo[metric]) {
      return;
    }

    // Guard: sessionCreatedAt is null before session.create() completes.
    // Date.now() - null = Date.now() (NaN-free but meaningless).
    // Return undefined so the metric is skipped rather than recorded as garbage.
    if (!store.sessionCreatedAt) {
      console.warn(
        `[Analytics] trackTimeTo(${metric}) skipped - no session start time`
      );
      return;
    }

    const timeTo = (Date.now() - store.sessionCreatedAt) / 1000;
    this.storedTimeTo[metric] = timeTo;

    // Forward to Sentry as breadcrumb for debugging context
    sentryService.addBreadcrumb(
      'analytics.timing',
      `Time to ${metric}: ${timeTo.toFixed(2)}s`,
      {
        metric,
        time_seconds: timeTo,
        // Preserve full session IDs in Sentry; they join analytics events
        // to Postgres rows and backend request logs.
        session_id: store.sessionId,
      }
    );

    // Track as Sentry metric for aggregated performance monitoring
    sentryService.trackMetric(`analytics.time_to.${metric}`, timeTo, 'second', {
      metric_type: 'time_to',
    });

    return this.addAnalyticsDataToQueue({ [metric]: timeTo }, options);
  }

  /**
   * Track an incremental counter metric.
   * Forwards to Sentry as breadcrumb and counter metric.
   * @param {string} metric - Metric name from ANALYTICS_METRICS
   * @param {Object} options - Optional configuration (e.g., { immediate: true })
   * @returns {Promise}
   */
  trackIncrement(metric, options) {
    const updatedIncrement = this.storedIncrements[metric] + 1 || 1;
    this.storedIncrements[metric] = updatedIncrement;
    const data = { [metric]: updatedIncrement };

    // Forward to Sentry as breadcrumb
    sentryService.addBreadcrumb(
      'analytics.increment',
      `${metric} incremented to ${updatedIncrement}`,
      {
        metric,
        count: updatedIncrement,
      }
    );

    // Track as Sentry counter for aggregated monitoring
    sentryService.incrementCounter(`analytics.${metric}`);

    return this.addAnalyticsDataToQueue(data, options);
  }

  /**
   * Track a boolean flag metric (tracked once per session).
   * Forwards to Sentry as breadcrumb.
   * @param {string} metric - Metric name from ANALYTICS_METRICS
   * @param {boolean|Object} [valueOrOptions=true] - Explicit value or legacy submission options.
   * @param {Object} [options] - Submission options when an explicit value is supplied.
   * @returns {Promise|undefined}
   */
  trackBoolean(metric, valueOrOptions = true, options = undefined) {
    const value = typeof valueOrOptions === 'boolean' ? valueOrOptions : true;
    const submissionOptions =
      typeof valueOrOptions === 'boolean' ? options : valueOrOptions;
    if (
      metric in this.storedBooleans &&
      this.storedBooleans[metric] === value
    ) {
      return;
    }
    this.storedBooleans[metric] = value;

    // Forward to Sentry as breadcrumb for user journey tracking
    sentryService.addBreadcrumb('analytics.flag', `Flag set: ${metric}`, {
      metric,
      value,
    });

    return this.addAnalyticsDataToQueue({ [metric]: value }, submissionOptions);
  }

  /**
   * Track arbitrary data metrics.
   * Forwards to Sentry as breadcrumb with sanitized data.
   * @param {Object} data - Key-value pairs of metrics
   * @param {Object} options - Optional configuration (e.g., { immediate: true })
   * @returns {Promise}
   */
  trackData(data, options) {
    // Forward to Sentry as breadcrumb (sanitize sensitive data)
    track('[Analytics] Sentry addBreadcrumb failed:', () => {
      const sanitizedData = this._sanitizeDataForSentry(data);
      const metricNames = Object.keys(data).join(', ');
      sentryService.addBreadcrumb(
        'analytics.data',
        `Data tracked: ${metricNames}`,
        sanitizedData
      );
    });

    return this.addAnalyticsDataToQueue(data, options);
  }

  /**
   * Add analytics data to the submission queue and trigger submission.
   *
   * Captures store.sessionId at enqueue time so that the eventual API call
   * uses the correct session even if the throttled submission fires after
   * session transition.
   *
   * @param {Object} data - Key-value pairs to queue
   * @param {Object} options - { immediate: true } to bypass throttle
   * @returns {Promise}
   */
  addAnalyticsDataToQueue(data, options) {
    // Capture session ID at enqueue time (not at submission time).
    // This prevents session N data from being sent with session N+1's ID
    // when the throttled submission fires after a session transition.
    const currentSessionId = store.sessionId;
    if (currentSessionId) {
      this._capturedSessionId = currentSessionId;
    }

    const currentKeys = Object.keys(this.dataSubmissionQueue);
    const newKeys = Object.keys(data);

    // Enforce queue size limit: allow updates to existing keys, but drop truly new keys
    // that would push the queue over MAX_QUEUE_KEYS (oldest-first eviction is not needed
    // because the queue is a flat map -- keys overwrite, they don't accumulate duplicates)
    if (currentKeys.length >= this.MAX_QUEUE_KEYS) {
      const newOnlyKeys = newKeys.filter(
        (k) => !Object.hasOwn(this.dataSubmissionQueue, k)
      );
      if (newOnlyKeys.length > 0) {
        console.warn(
          `[Analytics] Queue at capacity (${currentKeys.length}/${this.MAX_QUEUE_KEYS}), dropping new keys:`,
          newOnlyKeys
        );
        // Only merge keys that already exist in the queue (value updates are always allowed)
        const filteredData = {};
        for (const key of newKeys) {
          if (Object.hasOwn(this.dataSubmissionQueue, key)) {
            filteredData[key] = data[key];
          }
        }
        this.dataSubmissionQueue = {
          ...this.dataSubmissionQueue,
          ...filteredData,
        };
      } else {
        // All keys already exist -- safe to merge (value updates only)
        this.dataSubmissionQueue = { ...this.dataSubmissionQueue, ...data };
      }
    } else {
      this.dataSubmissionQueue = { ...this.dataSubmissionQueue, ...data };
    }

    if (options?.immediate) {
      // Catch errors from immediate submission to prevent unhandled rejections
      // from propagating to Vue components that may have already unmounted.
      // The data stays in the queue on failure and will be retried by the
      // next throttled call or flush.
      return this.submitAnalyticsData().catch((error) =>
        warnMessageOf(
          '[Analytics] Immediate submission failed (will retry):',
          error
        )
      );
    }
    this.submitAnalyticsDataThrottled();
    return Promise.resolve();
  }

  /**
   * Submit queued analytics data to the backend.
   *
   * Uses a submission promise (_submitPromise) to prevent concurrent calls
   * from the throttled path and the direct/flush path from racing on the queue.
   * When a submission is already in-flight, concurrent callers await the existing
   * promise instead of starting a duplicate API call.
   *
   * @returns {Promise}
   */
  async submitAnalyticsData() {
    // If a submission is already in-flight, return its promise so the caller
    // awaits the same result. This prevents flush() from resolving immediately
    // with undefined when the throttled path is mid-submission.
    if (this._submitPromise) {
      return this._submitPromise;
    }

    this._submitPromise = this._doSubmit();
    try {
      return await this._submitPromise;
    } finally {
      this._submitPromise = null;
    }
  }

  /**
   * Internal submission logic. Callers use submitAnalyticsData() which
   * provides the concurrency lock.
   * @private
   * @returns {Promise}
   */
  async _doSubmit() {
    return sentryService.withSpan('analytics.submit', 'analytics', async () => {
      const dataToSubmit = { ...this.dataSubmissionQueue };
      if (isEmpty(dataToSubmit)) {
        return;
      }

      // Customer-download portal mode: route through the bounded-context
      // endpoint (auth = download_code path param, no kiosk api_key). The
      // download_code lookup is the only identity we have on customer
      // phones — store.sessionId is populated later, after session.continue
      // resolves, but the queue may already hold engagement events.
      const customerCode = this._resolveCustomerDownloadCode();

      // Use session ID captured at enqueue time, falling back to store
      // only if nothing was captured (first submission before any enqueue).
      const sessionId = this._capturedSessionId || store.sessionId;

      if (!(customerCode || sessionId)) {
        console.warn('[Analytics] Cannot submit - session not initialized yet');
        sentryService.addBreadcrumb(
          'analytics.submit',
          'Submission skipped - no session ID',
          {
            queued_metrics: Object.keys(dataToSubmit).length,
          },
          'warning'
        );
        return; // Keep data in queue, retry on next call
      }

      const submissionStartTime = performance.now();
      const metricCount = Object.keys(dataToSubmit).length;

      try {
        if (customerCode) {
          // Filter to fields the customer surface accepts. The backend
          // DTO uses extra="forbid" so any operator-only field in the
          // queue would 422 the entire batch. Drop them quietly here so
          // a stray queue entry (e.g. a kiosk-time analytic that leaked
          // into the customer page somehow) doesn't poison the flush.
          const allowed = filterCustomerDownloadFields(dataToSubmit);
          if (!isEmpty(allowed)) {
            await restApi.postCustomerDownloadEngagement(customerCode, allowed);
          }
        } else if (authConfig.getApiKey()) {
          await restApi.updateSessionById(sessionId, dataToSubmit);
        } else {
          // No customer download_code and no kiosk api_key: the operator
          // session PATCH route would 401. Keep the batch queued
          // (a later flush, once the code resolves from the URL, can still
          // send it) rather than spam auth failures. Mirrors the
          // no-session skip above.
          return;
        }

        this._dropSubmitted(dataToSubmit);

        // Track successful submission timing
        const duration = performance.now() - submissionStartTime;
        sentryService.trackMetric(
          'analytics.submission_time',
          duration,
          'millisecond',
          {
            metric_count: metricCount,
          }
        );
      } catch (error) {
        this._submissionFailureCount++;
        const isCustomerNotFound = customerCode && httpStatusOf(error) === 404;
        // The API layer already captured this throw; a network failure it
        // dropped without stamping the flag is classified again below.
        const handledUpstream = error?._sentryCaptured === true;

        this._reportSubmitFailure(error, {
          isCustomerNotFound,
          handledUpstream,
          dataToSubmit,
          metricCount,
          sessionId,
        });

        if (customerCode) {
          if (isCustomerNotFound) {
            this._dropSubmitted(dataToSubmit);
          }
          return;
        }

        throw error;
      }
    });
  }

  /**
   * TOCTOU-safe queue cleanup: only remove keys whose values have NOT been
   * updated by addAnalyticsDataToQueue() since the snapshot was taken. If a
   * key was updated, its new value must be preserved for the next submission
   * cycle. O(n) in the snapshot size.
   * @private
   */
  _dropSubmitted(snapshot) {
    const keysToRemove = Object.keys(snapshot).filter(
      (key) => this.dataSubmissionQueue[key] === snapshot[key]
    );
    if (keysToRemove.length > 0) {
      this.dataSubmissionQueue = omit(this.dataSubmissionQueue, keysToRemove);
    }
  }

  /**
   * Telemetry for a failed _doSubmit. The batch is still queued at this
   * point whatever the cause; only how loudly we report differs.
   * @private
   */
  _reportSubmitFailure(
    error,
    {
      isCustomerNotFound,
      handledUpstream,
      dataToSubmit,
      metricCount,
      sessionId,
    }
  ) {
    // Always log to console so kiosk operators can see failures in DevTools
    console.warn(
      `[Analytics] Submission failed (attempt ${this._submissionFailureCount}):`,
      error.message || error
    );

    if (isCustomerNotFound || handledUpstream) {
      return;
    }

    // A phone going offline or backgrounding the tab mid-request is
    // expected and self-heals: the batch stays queued for the next
    // throttled submit or the unload beacon. Capturing it produced
    // KIOSK-FRONTEND-10A/10B/ES (thousands of "Load failed" events)
    // with nothing to act on, so it only leaves a breadcrumb — except the
    // first one seen while online, see onlineNetworkFailureReported.
    if (isNetworkFailure(error)) {
      const offline = navigator.onLine === false;
      track('[Analytics] Sentry addBreadcrumb failed:', () => {
        sentryService.addBreadcrumb(
          'analytics.submit',
          `Submission failed on the network (attempt ${this._submissionFailureCount}), batch kept queued`,
          {
            failure_count: this._submissionFailureCount,
            metric_count: metricCount,
            offline,
          },
          'warning'
        );
      });
      if (!(offline || onlineNetworkFailureReported)) {
        onlineNetworkFailureReported = true;
        track('[Analytics] Sentry captureMessage failed:', () => {
          sentryService.captureMessage(
            `Analytics submission failed on the network while online: ${error.message || error}`,
            {
              level: 'warning',
              tags: { analytics_failure: true, analytics_network_online: true },
              extra: {
                failure_count: this._submissionFailureCount,
                metric_count: metricCount,
                queued_metrics: Object.keys(dataToSubmit),
                // Full session ID is intentional observability metadata.
                session_id: sessionId,
              },
              fingerprint: ['analytics-submit-network-failure-online'],
            }
          );
        });
      }
      return;
    }

    track('[Analytics] Sentry captureException failed:', () => {
      sentryService.captureException(error, {
        tags: {
          analytics_failure: true,
          failure_count: this._submissionFailureCount,
        },
        extra: {
          queued_metrics: Object.keys(dataToSubmit),
          metric_count: metricCount,
          // Full session ID is intentional observability metadata.
          session_id: sessionId,
        },
        level: this._submissionFailureCount > 3 ? 'error' : 'warning',
      });
    });
  }

  /**
   * Flush all pending analytics data immediately.
   *
   * Concurrency-safe: if a flush is already in progress, returns the existing
   * promise without creating duplicate API calls or Sentry spans.
   * The isFlushInProgress check is OUTSIDE withSpan() to avoid creating
   * a redundant span for the no-op path.
   *
   * @param {number} timeoutMs - Maximum time to wait for flush (default: 2000ms)
   * @returns {Promise}
   */
  async flush(timeoutMs = 2000) {
    // Check OUTSIDE withSpan to avoid creating a useless span for duplicate calls.
    // This also prevents the second call from going through withSpan overhead.
    if (this.isFlushInProgress && this.currentFlushPromise) {
      return this.currentFlushPromise;
    }

    return sentryService.withSpan('analytics.flush', 'analytics', async () => {
      // Double-check after entering span (another call could have started
      // between the outer check and this point)
      if (this.isFlushInProgress && this.currentFlushPromise) {
        return this.currentFlushPromise;
      }

      // Cancel pending throttled call to prevent it from racing with flush
      if (this.submitAnalyticsDataThrottled.cancel) {
        this.submitAnalyticsDataThrottled.cancel();
      }

      const queuedMetricCount = Object.keys(this.dataSubmissionQueue).length;

      // Add breadcrumb for flush initiation
      sentryService.addBreadcrumb(
        'analytics.flush',
        `Flushing ${queuedMetricCount} queued metrics`,
        {
          queued_count: queuedMetricCount,
          timeout_ms: timeoutMs,
        }
      );

      // Set in-progress flag and store promise
      this.isFlushInProgress = true;

      // Track timeout timer so it can be cleared if submission finishes first
      // or if resetForNewSession is called (prevents firing into a new session)
      let timeoutId;
      const timeoutPromise = rejectAfter(
        timeoutMs,
        'Analytics flush timeout',
        (id) => {
          timeoutId = id;
        }
      );
      this._flushTimeoutId = timeoutId;

      this.currentFlushPromise = Promise.race([
        this.submitAnalyticsData(),
        timeoutPromise,
      ])
        .catch((error) => {
          console.error('[Analytics] Flush failed:', error);

          // Capture flush failures with context
          track('[Analytics] Sentry captureException failed:', () => {
            sentryService.captureException(error, {
              tags: {
                analytics_flush_failure: true,
              },
              extra: {
                timeout_ms: timeoutMs,
                queued_count: queuedMetricCount,
                is_timeout: error.message === 'Analytics flush timeout',
              },
              level: 'warning',
            });
          });

          // Don't block navigation on analytics failure
          return Promise.resolve();
        })
        .finally(() => {
          // Clear the timeout timer to prevent it from firing after flush completes
          // (avoids rejection into a potentially different session context)
          clearTimeout(timeoutId);
          this._flushTimeoutId = null;
          // Clean up flags after flush completes
          this.isFlushInProgress = false;
          this.currentFlushPromise = null;
        });

      return this.currentFlushPromise;
    });
  }

  /**
   * Track error codes for the current session.
   * Forwards to Sentry as breadcrumb and captures as message.
   * @param {string|string[]} errorCodes - Error code(s) to track
   * @returns {Promise}
   */
  async trackError(errorCodes) {
    // Capture customer-download code AND sessionId at start so the eventual
    // submission uses the actor identity that was active at error time, not
    // at flush time. Customer-portal context wins when set — its surface
    // exists for callers who don't have an api_key.
    const customerCode = this._resolveCustomerDownloadCode();
    const sessionId = store.sessionId;

    return sentryService.withSpan(
      'analytics.trackError',
      'analytics',
      async () => {
        const data = (
          Array.isArray(errorCodes) ? errorCodes : [errorCodes]
        ).filter(Boolean);

        if (data.length === 0) {
          return;
        }

        // Guard against missing session - skip API call but still log to Sentry.
        // Customer-portal mode doesn't need a sessionId (the download_code
        // resolves the row server-side), so only the kiosk-mode branch
        // requires sessionId.
        if (!(customerCode || sessionId)) {
          console.warn('[Analytics] Cannot track error - no session ID');
          sentryService.addBreadcrumb(
            'analytics.error',
            `Error tracking skipped (no session): ${data.join(', ')}`,
            { error_codes: data },
            'warning'
          );
          return;
        }

        // Forward to Sentry as breadcrumb for debugging context
        sentryService.addBreadcrumb(
          'analytics.error',
          `Error codes tracked: ${data.join(', ')}`,
          {
            error_codes: data,
            // Full session ID is intentional observability metadata.
            session_id: sessionId,
          },
          'error'
        );

        // Don't fire as a Sentry issue. trackError() runs for every
        // analytics error code the kiosk emits — face_detection_no_faces,
        // api_error_printing, face_detection_server_error, etc. — many
        // of which are user conditions ("no face in photo") or
        // already-captured infra errors. The result was 5 separate
        // Sentry issues (KIOSK-FRONTEND-S, 1W, JJ, Y, plus the
        // -3G/EG/KX network failures of trackSessionErrors itself)
        // totalling ~8.5k events / 14d with no actionable signal —
        // they don't tell ops anything that isn't already in the
        // upstream issue. The breadcrumb above still preserves the
        // error code in the eventual Sentry event for the *real*
        // exception that triggered the trackError call.

        try {
          if (customerCode) {
            await restApi.postCustomerAnalyticsErrors(customerCode, data);
          } else if (authConfig.getApiKey()) {
            await restApi.trackSessionErrors(sessionId, data);
          } else {
            // No customer download_code and no kiosk api_key: the operator
            // analytics/errors route would 401 ("Kiosk API key
            // required") on a customer phone, and that 401 would itself be
            // re-tracked — the KIOSK-FRONTEND-Y* feedback flood. Telemetry
            // is best-effort; the breadcrumb above already preserved the
            // codes on the real exception, so drop silently.
            return;
          }
        } catch (error) {
          if (customerCode) {
            return;
          }

          // A 401 here only means we lacked creds for the chosen route;
          // surfacing it adds noise (and would re-enter this path), not
          // signal. Everything else is a genuine tracking failure.
          if (httpStatusOf(error) !== 401) {
            sentryService.captureException(error, {
              tags: {
                analytics_error_tracking_failure: true,
              },
              extra: {
                error_codes: data,
              },
              level: 'error',
            });
          }
          throw error;
        }
      }
    );
  }

  /**
   * Flush pending analytics data for a specific session ID, bypassing the store lookup.
   * Used by Session.end() to drain the queue BEFORE clearing session state, ensuring
   * buffered events from session N are never accidentally sent with session N+1's identity.
   *
   * This is a best-effort fire-and-forget: on timeout or error the data is lost for
   * the ending session (acceptable) rather than bleeding into the next session (not acceptable).
   *
   * Coordination with flush(): if flush() is already in-flight, we wait for it to
   * complete (it may succeed and drain the queue). Then we send any remaining data
   * with the explicit sessionId.
   *
   * @param {string} sessionId - The session ID to flush for (captured before session state is cleared)
   * @param {number} timeoutMs - Maximum time to wait (default: 1500ms, shorter than normal flush)
   * @returns {Promise<void>}
   */
  async flushForSession(sessionId, timeoutMs = 1500) {
    // Cancel any pending throttled submission to prevent it from firing after reset
    if (this.submitAnalyticsDataThrottled.cancel) {
      this.submitAnalyticsDataThrottled.cancel();
    }

    // If flush() is already in progress (e.g., from router guard), wait for it
    // to complete before doing our own submission. This prevents two concurrent
    // API calls racing on the same queue. The flush might succeed and drain
    // the queue, making our call a no-op.
    if (this.isFlushInProgress && this.currentFlushPromise) {
      try {
        await this.currentFlushPromise;
      } catch (_e) {
        // flush() already handles its own errors
      }
    }

    const dataToSubmit = { ...this.dataSubmissionQueue };
    if (isEmpty(dataToSubmit) || !sessionId) {
      return;
    }

    const metricCount = Object.keys(dataToSubmit).length;

    sentryService.addBreadcrumb(
      'analytics.flushForSession',
      `Flushing ${metricCount} metrics for ending session`,
      {
        // Full session ID is intentional observability metadata.
        session_id: sessionId,
        metric_count: metricCount,
      }
    );

    let timeoutId;
    try {
      await Promise.race([
        restApi.updateSessionById(sessionId, dataToSubmit),
        rejectAfter(timeoutMs, 'flushForSession timeout', (id) => {
          timeoutId = id;
        }),
      ]);
      this._dropSubmitted(dataToSubmit);
    } catch (error) {
      // Best-effort: log and discard. The data is lost for this session, which is
      // preferable to it bleeding into the next session.
      console.warn(
        '[Analytics] flushForSession failed (data discarded):',
        error.message || error
      );
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Best-effort keepalive flush of queued metrics during page unload
   * (beforeunload / pagehide), where the throttled submit and async/await
   * can't reliably finish. Picks the endpoint by context so a customer
   * phone never beacons the kiosk-auth operator route:
   *
   *  - Customer-download portal: the keyless, download_code-authenticated
   *    customer endpoint. A beacon to the operator session PATCH would 401
   *    ("Kiosk API key required") and re-snowball into the
   *    KIOSK-FRONTEND-Y* / EE flood — the customer's phone has no api_key.
   *  - Kiosk operator page: PATCH /api/v1/sessions/{id} with the api_key header.
   *
   * Clears the queue optimistically (the page is leaving; no retry is
   * possible) and swallows all errors. No-op when the queue is empty or no
   * identity is resolvable for the chosen route.
   *
   * @param {Object} context
   * @param {boolean} context.isCustomerFacingPage - router.isCustomerFacingPage
   * @param {string|null} [context.apiKey] - authConfig.getApiKey()
   * @returns {Promise<void>|undefined} The in-flight beacon, or undefined on no-op.
   */
  flushQueuedMetricsOnUnload({ isCustomerFacingPage, apiKey = null }) {
    const data = this.dataSubmissionQueue;
    if (!data || Object.keys(data).length === 0) {
      return undefined;
    }

    if (isCustomerFacingPage) {
      const code = this._resolveCustomerDownloadCode();
      const allowed = code ? filterCustomerDownloadFields(data) : {};
      if (!code || Object.keys(allowed).length === 0) {
        return undefined;
      }
      this.dataSubmissionQueue = {};
      return sendKeepalive(
        `/api/v1/customer/sessions/${code}/engagement`,
        allowed,
        { apiKey: null }
      ).then(() => undefined);
    }

    if (!store.sessionId) {
      return undefined;
    }
    this.dataSubmissionQueue = {};
    return sendKeepalive(`/api/v1/sessions/${store.sessionId}`, data, {
      method: 'PATCH',
      apiKey,
      sessionId: store.sessionId,
    }).then(() => undefined);
  }

  /**
   * Enter customer-download portal mode. All subsequent submits use
   * the bounded-context /api/v1/customer/sessions/{download_code}/...
   * endpoints (no kiosk api_key). Cleared on resetForNewSession() so a
   * kiosk session that follows can't accidentally inherit the routing.
   *
   * Validates the shape (8 lowercase alphanumeric) before setting so a
   * bogus code at the route layer is caught here instead of at the
   * first failed API call.
   *
   * @param {string} downloadCode - 8-char base32 code from the URL
   */
  setCustomerDownloadCode(downloadCode) {
    const normalized = (downloadCode || '').toLowerCase();
    if (!/^[a-z0-9]{8}$/.test(normalized)) {
      console.warn(
        `[Analytics] setCustomerDownloadCode: invalid code shape`,
        downloadCode?.length
      );
      this._customerDownloadCode = null;
      return;
    }
    this._customerDownloadCode = normalized;
    sentryService.addBreadcrumb(
      'analytics.customer_download',
      'Customer download portal mode enabled',
      { download_code_length: normalized.length }
    );
  }

  /**
   * Resolve the active customer-download code, preferring the value the
   * view set via setCustomerDownloadCode() but falling back to the URL.
   *
   * The fallback closes a race: a queued analytics flush or a late
   * trackError() can land AFTER the download view cleared
   * _customerDownloadCode (beforeUnmount / resetForNewSession) while
   * store.sessionId still holds the just-viewed session. Without it those
   * stragglers route to the kiosk-auth operator endpoints
   * (session analytics/errors, session PATCH), which 401 on a
   * customer phone that has no api_key — the KIOSK-FRONTEND-Y* / EE / EC
   * flood. Re-deriving from the URL keeps them on the
   * download_code-authenticated customer surface for as long as the
   * customer is actually on the page; once they navigate away the path
   * stops matching and this returns null.
   *
   * Mirrors getDownloadCodeFromRouter() in CustomerPortraitsDownloadCode:
   * lowercase, strip non-alphanumerics (covers a Unicode-minus or dash a
   * customer pasted into the code), then require the 8-char base32 shape.
   *
   * @returns {string|null} 8-char base32 code, or null off the portal.
   * @private
   */
  _resolveCustomerDownloadCode() {
    if (this._customerDownloadCode) {
      return this._customerDownloadCode;
    }
    const path =
      (typeof window !== 'undefined' && window.location?.pathname) || '';
    const match = /\/customer\/portraits-download\/([^/?#]+)/.exec(path);
    if (!match) {
      return null;
    }
    let raw = match[1];
    try {
      raw = decodeURIComponent(raw);
    } catch (_e) {
      // Malformed percent-encoding — fall through; the strip + validate
      // below rejects anything that isn't a clean 8-char code anyway.
    }
    const normalized = raw.toLowerCase().replace(/[^0-9a-z]/g, '');
    return /^[a-z0-9]{8}$/.test(normalized) ? normalized : null;
  }

  /**
   * Reset analytics state for a new session.
   * Cancels pending throttled submissions, aborts in-flight flush operations,
   * and clears all stored metrics, queue, and timers.
   * MUST be called after flushForSession() to avoid discarding unflushed data.
   */
  resetForNewSession() {
    // Cancel pending throttled submission to prevent it from firing into the new session
    if (this.submitAnalyticsDataThrottled.cancel) {
      this.submitAnalyticsDataThrottled.cancel();
    }

    // Clear any in-flight flush timeout to prevent it from rejecting into a new session
    if (this._flushTimeoutId) {
      clearTimeout(this._flushTimeoutId);
      this._flushTimeoutId = null;
    }

    // Reset flush state. If flush() was in-progress, its .finally() will
    // set isFlushInProgress=false and currentFlushPromise=null, but by that
    // time the queue is already empty and _capturedSessionId is null, so
    // the stale submission will see isEmpty(dataToSubmit) and bail.
    // We reset these flags proactively so that new flush() calls after reset
    // don't see stale in-progress state.
    this.isFlushInProgress = false;
    this.currentFlushPromise = null;
    this._submitPromise = null;

    const droppedKeys = Object.keys(this.dataSubmissionQueue);
    if (droppedKeys.length > 0) {
      console.warn(
        `[Analytics] resetForNewSession discarding ${droppedKeys.length} queued metrics:`,
        droppedKeys
      );
    }

    this.storedTimeTo = {};
    this.storedIncrements = {};
    this.storedBooleans = {};
    this.dataSubmissionQueue = {};
    this._submissionFailureCount = 0;
    this._capturedSessionId = null;
    // Customer-download mode is per-portal-mount; clear on session reset
    // so a kiosk operator session that runs next can't accidentally post
    // to the customer endpoint with a leftover download_code.
    this._customerDownloadCode = null;
    resetOnlineNetworkFailureReport();

    sentryService.addBreadcrumb(
      'analytics.reset',
      'Analytics state reset for new session',
      { dropped_metrics: droppedKeys.length }
    );
  }

  /**
   * Keep analytics metadata intact before sending to Sentry.
   * @param {Object} data - Data to sanitize
   * @returns {Object} - Data with full operational identifiers preserved
   * @private
   */
  _sanitizeDataForSentry(data) {
    // Do not re-mask device_uuid, remote_ip, user_agent, or
    // session/ticket/download identifiers here. They are operational support
    // metadata and are required in full for Sentry, analytics, and DB joins.
    return { ...data };
  }
}

export const analytics = new Analytics();
